/**
 * NotificationContext - In-App Notifications State
 *
 * Phase 3.1 - Thin adapter over React Query
 *
 * Features:
 * - React Query is the single source of truth for unreadCount
 * - Cross-tab sync via BroadcastChannel (updates React Query cache)
 * - No polling (per requirements)
 * - No independent useState for unreadCount
 */

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import {
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../hooks/useNotificationsQuery';
import { useNotificationsRealtime } from '../hooks/useNotificationsRealtime';
import { queryKeys } from '../lib/queryKeys';

// ============================================
// TYPES
// ============================================

export interface NotificationUser {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface Notification {
  id: string;
  type: 'new_follower' | 'recommendation' | 'milestone';
  body: string;
  data: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  from_user: NotificationUser | null;
}

export interface NotificationContextValue {
  // State
  unreadCount: number;
  isLoading: boolean;

  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetchUnreadCount: () => Promise<void>;
}

// ============================================
// CONTEXT
// ============================================

const NotificationContext = createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Realtime subscription (Phase 3.1c - pushes into React Query cache)
  useNotificationsRealtime();

  // React Query hooks (Phase 3.1 - single source of truth)
  const { unreadCount, isLoading: countLoading } = useUnreadNotificationCount();
  const { markAsRead: markAsReadMutation, isLoading: markReadLoading } = useMarkNotificationRead();
  const { markAllAsRead: markAllAsReadMutation, isLoading: markAllLoading } = useMarkAllNotificationsRead();

  // Combined loading state
  const isLoading = countLoading || markReadLoading || markAllLoading;

  // Set up BroadcastChannel for cross-tab sync
  // IMPORTANT: BroadcastChannel updates React Query cache, NOT local state
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannelRef.current = new BroadcastChannel('filmple:notifications');
      broadcastChannelRef.current.onmessage = (event) => {
        // Sync notification state across tabs via React Query cache
        if (event.data.userId !== user?.id) return;

        const countKey = queryKeys.notifications.unreadCount(user?.id || '');

        if (event.data.type === 'notification_read') {
          queryClient.setQueryData<number>(countKey, (old) => Math.max(0, (old ?? 0) - 1));
        } else if (event.data.type === 'notifications_cleared') {
          queryClient.setQueryData<number>(countKey, 0);
        } else if (event.data.type === 'unread_count_updated') {
          queryClient.setQueryData<number>(countKey, event.data.count);
        }
      };
    }

    return () => {
      broadcastChannelRef.current?.close();
    };
  }, [user?.id, queryClient]);

  // Phase 5F: Track mount time to skip focus events on initial load
  const mountedAtRef = useRef<number>(Date.now());

  // BUG 2 FIX: Refetch unread count on window focus (fallback if realtime doesn't fire)
  // Phase 5F: Skip focus invalidation for first 2 seconds after mount to prevent badge flash
  useEffect(() => {
    if (!user?.id) return;

    const handleFocus = () => {
      // Skip focus events within 2 seconds of mount (prevents badge flash on hard refresh)
      const timeSinceMount = Date.now() - mountedAtRef.current;
      if (timeSinceMount < 2000) {
        return;
      }

      const countKey = queryKeys.notifications.unreadCount(user.id);
      queryClient.invalidateQueries({ queryKey: countKey });
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id, queryClient]);

  // Wrap mutations to broadcast to other tabs
  const markAsRead = useCallback(
    async (notificationId: string) => {
      const success = await markAsReadMutation(notificationId);
      if (success && user?.id) {
        broadcastChannelRef.current?.postMessage({
          type: 'notification_read',
          userId: user.id,
          notificationId,
        });
      }
    },
    [markAsReadMutation, user?.id]
  );

  const markAllAsRead = useCallback(async () => {
    const success = await markAllAsReadMutation();
    if (success && user?.id) {
      broadcastChannelRef.current?.postMessage({
        type: 'notifications_cleared',
        userId: user.id,
      });
    }
  }, [markAllAsReadMutation, user?.id]);

  // Expose refetch for manual refresh (invalidates React Query cache)
  const refetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    const countKey = queryKeys.notifications.unreadCount(user.id);
    await queryClient.invalidateQueries({ queryKey: countKey });
  }, [user?.id, queryClient]);

  const value: NotificationContextValue = useMemo(
    () => ({
      unreadCount,
      isLoading,
      markAsRead,
      markAllAsRead,
      refetchUnreadCount,
    }),
    [unreadCount, isLoading, markAsRead, markAllAsRead, refetchUnreadCount]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
